import { useSearch } from "@tanstack/react-router";
import { Button } from "antd";
import { useDestroyLabelMutation, useGetLabelQuery } from "api/TenantSlug/Label/hooks.ts";
import { EditLabelForm } from "components/Forms/LabelsForm/EditLabelForm.tsx";
import { Drawer } from "components/UI/Drawer";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { toast } from "react-toastify";
import { DrawerId, FormId } from "types/models.ts";

interface IEditLabelDrawerProps {
    onSuccess: () => void;
}

export const EditLabelDrawer: FC<IEditLabelDrawerProps> = ({ onSuccess }) => {
    const { t } = usePaldockTranslation("drawers.edit-label");
    const { t: tC } = usePaldockTranslation("common");
    const drawerId = DrawerId.EditLabel;
    const { hide } = useDrawer();

    const searchParams: { ["editLabel-labelId"]: string | undefined } = useSearch({ strict: false });
    const labelId = searchParams["editLabel-labelId"];

    const { isLoading, data } = useGetLabelQuery(labelId ?? "", {}, { enabled: Boolean(labelId) });
    const { mutate } = useDestroyLabelMutation();

    const handleDestroy = () => {
        if (!labelId) {
            return;
        }

        mutate(labelId, {
            onSuccess: () => {
                toast.success(t("messages.delete_success"));
                hide(drawerId, ["editLabel-labelId"]);
            },
            onError: () => {
                toast.error(t("messages.delete_error"));
            },
        });
    };

    const { label } = data || {};

    if (!label) {
        return null;
    }

    return (
        <Drawer
            loading={isLoading}
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={t("title")} subTitles={[label.name]} />}
            searchParamsToClean={["editLabel-labelId"]}
            extraButtons={
                <>
                    <Button danger size={"large"} type={"primary"} onClick={handleDestroy}>
                        {tC("delete")}
                    </Button>
                    <Button size={"large"} htmlType="submit" type={"primary"} form={FormId.EditLabel}>
                        {tC("save")}
                    </Button>
                </>
            }
        >
            <EditLabelForm label={label} onSuccess={onSuccess} />
        </Drawer>
    );
};
